import { availablePlacementsForType } from '../../combination-rules/engine'
import {
  EmbroideryLocation,
  EmbroideryPosition,
  PlacementId,
  toPlacementId,
} from '../../combination-rules/placementIds'
import { EmbroideryConstraintSet, EmbroideryTypeKey } from '../../combination-rules/types'
import { EmbroideryFormSubmissions } from '../../types'
import { makeEmbroideryItem } from '../makeEmbroideryItem'
import { EmbroideryItem } from '../types'

type SavedPlacement =
  | { location: EmbroideryLocation; position: EmbroideryPosition }
  | null
  | undefined

/**
 * Which placement a seeded preference lands on.
 */
const resolvePlacement = (
  typeKey: EmbroideryTypeKey,
  savedPlacement: SavedPlacement,
  seededSoFar: readonly EmbroideryItem[],
  constraints: EmbroideryConstraintSet
): PlacementId | undefined => {
  const available = availablePlacementsForType(typeKey, seededSoFar, constraints)
  const [firstAvailable] = available
  if (!firstAvailable) return undefined

  const saved = savedPlacement && toPlacementId(savedPlacement.location, savedPlacement.position)
  return available.find(placement => placement.id === saved)?.id ?? firstAvailable.id
}

/**
 * Reads the customer's saved account preferences as items that are already selected, so a blank
 * modal opens showing what they chose last time.
 *
 * Covers what `EmbroideryProvider.tsx`'s `prefilledFormValuesByKind` covers for the legacy modal,
 * but as committed items rather than form defaults, since the new modal has no per-field default.
 */
export const itemsFromPreferences = (
  savedEmbroideryPreferences: Partial<EmbroideryFormSubmissions> | null | undefined,
  constraints: EmbroideryConstraintSet
): EmbroideryItem[] => {
  // Seeded in order, because text can take the placement the logo preferred.
  const seeded: EmbroideryItem[] = []
  if (!savedEmbroideryPreferences) return seeded

  const { TEXT, LOGO_DTC } = savedEmbroideryPreferences

  const firstLine = TEXT?.textFirstLine.trim() ? TEXT.textFirstLine : undefined
  if (TEXT && firstLine) {
    const placementId = resolvePlacement('text', TEXT.textPlacement, seeded, constraints)
    if (placementId) {
      const values = {
        firstLine,
        ...(TEXT.textSecondLine?.trim() ? { secondLine: TEXT.textSecondLine } : {}),
        font: TEXT.textFont,
        threadColor: TEXT.textColor,
      }
      seeded.push(makeEmbroideryItem({ typeKey: 'text', placementId, values }, 'PREFERENCE'))
    }
  }

  if (LOGO_DTC?.logoFile) {
    const placementId = resolvePlacement('logo', LOGO_DTC.logoPlacement, seeded, constraints)
    if (placementId) {
      const values = { logoFile: LOGO_DTC.logoFile, requiresSetup: false }
      seeded.push(makeEmbroideryItem({ typeKey: 'logo', placementId, values }, 'PREFERENCE'))
    }
  }

  return seeded
}
